import type { SanityDocument } from '@sanity/client';
import { createClient } from '@sanity/client';
import JsonResponse from '../../js/cloudflare/JsonResponse';
import type { JsonApiFunction } from '../../js/cloudflare/types';
import type { ImageWithMetadata } from '../../js/sanity/components/SanityImage';
import type { TaggedGridItemDocument } from '../../js/sanity/grid/TaggedGridItem';
import contentSearch from '../../js/sanity/infrastructure/contentSearch';
import { getConfig } from '../../js/sanity/infrastructure/getConfig';
import type { SanityProps } from '../../js/sanity/infrastructure/getSanityPageProps';
import type { KnownTypes } from '../../js/sanity/page';

interface ListPage {
    _id: string;
    path: string;
    title: string;
}

interface DrinkRecipeItem extends SanityDocument {
    _type: 'drinkRecipe';
    title: string;
    bannerImage: ImageWithMetadata;
    slug: { current: string };
    listPage: ListPage;
}

interface GrapeItem extends SanityDocument {
    _type: 'grape';
    name: string;
    slug: { current: string };
    summary: string;
    grapeType: 'blue' | 'green';
    listPage: ListPage;
}

interface Definition extends SanityDocument {
    _type: 'definition';
    name: string;
    slug: { current: string };
    summary: string;
    listPage: ListPage;
}

export type SearchResultItem = TaggedGridItemDocument<KnownTypes> | DrinkRecipeItem | GrapeItem | Definition;

export interface SanitySearchPageResult extends SanityDocument {
    items: SearchResultItem[];
    query: string;
}
export type ResponseBody = SanityProps<SanitySearchPageResult>;

export const onRequestGet: JsonApiFunction<typeof pathname, unknown, ResponseBody> = async (ctx) => {
    const url = new URL(ctx.request.url);

    if (ctx.request.headers.get('accept') !== 'application/json') {
        const queryParams = new URLSearchParams(url.searchParams);
        queryParams.set('searchType', 'content');
        return Response.redirect(new URL(`/search?${queryParams.toString()}`, ctx.request.url), 302);
    }

    const ids = url.searchParams.getAll('id') ?? '';
    const clientConfig = getConfig(ctx);

    const client = createClient(clientConfig);
    const response = await contentSearch(client, { results: ids.map((id, index) => ({ id, score: -index })), term: '' }, clientConfig, ctx);

    return new JsonResponse<ResponseBody>(response, {
        headers: {
            'Cache-Control': `public, s-maxage=${5 * 60}, stale-while-revalidate=30`,
        },
    });
};

export const pathname = '/search/content';
